import { LinearGradient } from 'expo-linear-gradient';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  Vibration,
  View,
} from 'react-native';
import { Audio } from 'expo-av';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from '../v10/theme';
import type { ToolProps } from '../olmps/ToolHost';
import { cancelTimerNotification, scheduleTimerNotification } from '../lib/notifications';

// Full-fidelity native replica of the web THEMIS protocols experience
// (web reference: src/app/protocols/page.tsx, src/app/protocols/[protocolId]/page.tsx,
// src/context/protocol-runner-context.tsx), adapted to a phone-width panel.
//
// Parity notes:
//  - Timer audio: web plays a looping alarm (`playSound`/`stopSound`, an
//    <audio loop> element) when a step's timer expires with transition
//    OFF, and a one-shot double-beep chime (`playTransitionBell`, Web Audio
//    oscillator) when transition is ON. There is no bundled sound asset
//    under apps/mobile/assets, so both cues are reproduced here with a
//    short generated 880Hz sine-wave beep encoded as a base64 WAV data URI
//    played via expo-av — looped for the no-transition case, played once
//    for the transition case — with Vibration as an always-on fallback cue
//    (web has no vibration; this is additive, matching the spirit of "the
//    same cue" on a device where audio may be muted/silent-switched).
//  - protocol_schedules UI: full parity — per-protocol list of recurring
//    day/time schedule instances (add/edit time/toggle days/delete),
//    reading/writing the same `protocol_schedules` table
//    (id, user_id, protocol_id, instances jsonb) as web
//    (src/app/protocols/[protocolId]/page.tsx handleAddScheduleInstance /
//    handleUpdateScheduleInstance / handleToggleScheduleDay /
//    handleDeleteScheduleInstance), reachable from the same action menu.
//  - Archive: matches web's Archive exactly — same `trash` table row as
//    Delete, but with `kind: 'archive'` instead of `kind: 'trash'`
//    (src/lib/trash.ts moveToArchive / moveToTrash), and the same cascade
//    of protocol_items / protocol_completions / protocol_schedules deletes
//    before the protocol row itself is removed
//    (src/app/protocols/page.tsx deleteProtocol). Archive sits alongside
//    Delete in the protocol row's "⋯" menu, same as web's dropdown.
//  - Backgrounded/killed-app alerts (closed 2026-07-14): every time a step
//    timer is (re)armed — startProtocol, advanceToNextStep, handleRepeat —
//    a local notification ("<protocol title> — step timer done") is
//    scheduled via scheduleTimerNotification (src/lib/notifications.ts) for
//    that step's wall-clock expiry (Date.now() + remainingSeconds*1000,
//    computed at arm time). If the in-app timer tick reaches the same
//    expiry first (app foregrounded), handleTimerExpired cancels the
//    pending notification before the in-app alarm/vibration fires, so the
//    two never double up. Next / Repeat / Abandon, and pause, all cancel the
//    pending notification for the step being left.
//
// Delete/Archive snapshot the protocol row into `trash` before cascading
// (matching src/lib/trash.ts moveToTrash/moveToArchive + deleteProtocol),
// so the protocol is restorable from Trash/Archive on web. Steps,
// completion log, and schedules are still hard-deleted, same as web.

const PROTOCOLS_TABLE = 'protocols';
const GROUPS_TABLE = 'protocol_groups';
const ITEMS_TABLE = 'protocol_items';
const COMPLETIONS_TABLE = 'protocol_completions';
const SCHEDULES_TABLE = 'protocol_schedules';
const TRASH_TABLE = 'trash';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// A short (~0.3s) generated 880Hz sine-wave beep, 8kHz mono 16-bit PCM WAV,
// used as the native stand-in for web's audio cues (see parity notes above).
const BEEP_WAV_DATA_URI =
  'data:audio/wav;base64,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';

interface ProtocolRow {
  id: string;
  title: string;
  group_id: string | null;
  order: number | null;
  notes?: string | null;
  transitionEnabled?: boolean | null;
  transitionSeconds?: number | null;
}

interface ProtocolGroupRow {
  id: string;
  name: string;
  order: number | null;
}

interface ProtocolItemRow {
  id: string;
  protocol_id: string;
  title: string;
  done: boolean | null;
  duration_minutes: number | null;
  order: number | null;
  notes: string | null;
}

interface ProtocolCompletionRow {
  id: string;
  protocol_id: string;
  protocol_title: string;
  started_at: string;
  completed_at: string | null;
  status: 'success' | 'abandoned';
  steps_completed: number;
  steps_repeated: Record<string, number>;
  total_duration_seconds: number;
  pause_count: number;
}

interface ProtocolScheduleInstance {
  id: string;
  time: string;
  days: string[];
}

interface ProtocolScheduleRow {
  id: string;
  user_id: string;
  protocol_id: string;
  instances: ProtocolScheduleInstance[];
}

function formatTimer(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function formatDurationSeconds(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatLogDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  let h = d.getHours();
  const m = d.getMinutes();
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${h}:${String(m).padStart(2, '0')} ${suffix}`;
}

function sortByOrder<T extends { order: number | null }>(rows: T[]): T[] {
  return rows.slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

function Chip({ text, active, onPress }: { text: string; active: boolean; onPress: () => void }) {
  const styles = useStyles();
  return (
    <Pressable style={[styles.chip, active && styles.chipActive]} onPress={onPress}>
      <Text style={[styles.chipText, active && styles.chipTextActive]}>{text}</Text>
    </Pressable>
  );
}

// ===== Protocol list (top-level) =====

export function ThemisTool({ initialProtocolId }: ToolProps) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const { session } = useAuth();
  const userId = session?.user.id;

  const [protocols, setProtocols] = useState<ProtocolRow[]>([]);
  const [groups, setGroups] = useState<ProtocolGroupRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  // Preselect a protocol on mount when the caller knows which one (e.g.
  // StepScreen's Execute cell matched this run to a METIS item by title) —
  // additive only, `initialProtocolId` is absent for every other entry point.
  const [selectedProtocolId, setSelectedProtocolId] = useState<string | null>(initialProtocolId ?? null);

  const [addMode, setAddMode] = useState<'protocol' | 'group' | null>(null);
  const [addName, setAddName] = useState('');
  const [addGroupId, setAddGroupId] = useState<string | null>(null);

  const [editingProtocol, setEditingProtocol] = useState<ProtocolRow | null>(null);
  const [editName, setEditName] = useState('');
  const [editGroupId, setEditGroupId] = useState<string | null>(null);

  const [editingGroup, setEditingGroup] = useState<ProtocolGroupRow | null>(null);
  const [editGroupName, setEditGroupName] = useState('');

  const loadList = useCallback(async () => {
    if (!userId) {
      setProtocols([]);
      setGroups([]);
      setLoadingList(false);
      return;
    }
    setLoadingList(true);
    const [protoRes, groupRes] = await Promise.all([
      supabase
        .from(PROTOCOLS_TABLE)
        .select('id, title, group_id, order')
        .eq('user_id', userId)
        .order('order', { ascending: true }),
      supabase
        .from(GROUPS_TABLE)
        .select('id, name, order')
        .eq('user_id', userId)
        .order('order', { ascending: true }),
    ]);
    if (protoRes.error) {
      setListError(protoRes.error.message);
    } else {
      setListError(null);
      setProtocols((protoRes.data ?? []) as ProtocolRow[]);
    }
    if (groupRes.error) {
      setListError(groupRes.error.message);
    } else {
      setGroups((groupRes.data ?? []) as ProtocolGroupRow[]);
    }
    setLoadingList(false);
  }, [userId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  const toggleGroupExpand = (id: string) => {
    setExpandedGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openAddChooser = () => {
    Alert.alert('Add', undefined, [
      { text: 'Add Protocol', onPress: () => openAddProtocol() },
      { text: 'Add Group', onPress: () => openAddGroup() },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const openAddProtocol = () => {
    setAddName('');
    setAddGroupId(null);
    setAddMode('protocol');
  };

  const openAddGroup = () => {
    setAddName('');
    setAddMode('group');
  };

  const submitAdd = async () => {
    if (!userId || !addName.trim() || busy) return;
    setBusy(true);
    try {
      if (addMode === 'protocol') {
        const scope = protocols.filter((p) => (p.group_id ?? null) === addGroupId);
        const { error: err } = await supabase.from(PROTOCOLS_TABLE).insert({
          user_id: userId,
          title: addName.trim(),
          group_id: addGroupId,
          order: scope.length,
          created_at: new Date().toISOString(),
        });
        if (err) setListError(err.message);
      } else if (addMode === 'group') {
        const { error: err } = await supabase.from(GROUPS_TABLE).insert({
          user_id: userId,
          name: addName.trim(),
          order: groups.length,
          created_at: new Date().toISOString(),
        });
        if (err) setListError(err.message);
      }
      await loadList();
    } finally {
      setBusy(false);
      setAddMode(null);
      setAddName('');
      setAddGroupId(null);
    }
  };

  const startEditProtocol = (p: ProtocolRow) => {
    setEditingProtocol(p);
    setEditName(p.title);
    setEditGroupId(p.group_id ?? null);
  };

  const submitEditProtocol = async () => {
    if (!editingProtocol || !editName.trim() || !userId) return;
    const { error: err } = await supabase
      .from(PROTOCOLS_TABLE)
      .update({ title: editName.trim(), group_id: editGroupId })
      .eq('id', editingProtocol.id)
      .eq('user_id', userId);
    if (err) setListError(err.message);
    setEditingProtocol(null);
    setEditName('');
    setEditGroupId(null);
    await loadList();
  };

  const startEditGroup = (g: ProtocolGroupRow) => {
    setEditingGroup(g);
    setEditGroupName(g.name);
  };

  const submitEditGroup = async () => {
    if (!editingGroup || !editGroupName.trim() || !userId) return;
    const { error: err } = await supabase
      .from(GROUPS_TABLE)
      .update({ name: editGroupName.trim() })
      .eq('id', editingGroup.id)
      .eq('user_id', userId);
    if (err) setListError(err.message);
    setEditingGroup(null);
    setEditGroupName('');
    await loadList();
  };

  // Matches web's deleteProtocol(protocol, kind) (src/app/protocols/page.tsx):
  // both Delete and Archive snapshot the protocol row into `trash` — Archive
  // just tags the snapshot kind: 'archive' instead of kind: 'trash' — then
  // cascade-delete protocol_items, protocol_completions and
  // protocol_schedules, then the protocol row itself.
  const deleteProtocol = useCallback(
    async (protocol: ProtocolRow, kind: 'trash' | 'archive' = 'trash') => {
      if (!userId) return;
      // Snapshot the full protocol row into `trash` before cascading deletes,
      // matching web's soft-delete (src/lib/trash.ts moveToTrash/moveToArchive,
      // called from src/app/protocols/page.tsx deleteProtocol) so the protocol
      // is restorable from Trash/Archive on web. Fail closed: if the snapshot
      // fails, abort rather than delete without a trash copy.
      const { data: protocolRow, error: fetchErr } = await supabase
        .from(PROTOCOLS_TABLE)
        .select('*')
        .eq('id', protocol.id)
        .eq('user_id', userId)
        .maybeSingle();
      if (fetchErr) {
        setListError(fetchErr.message);
        return;
      }
      if (protocolRow) {
        const { error: trashErr } = await supabase.from(TRASH_TABLE).insert({
          user_id: userId,
          source_component: 'Protocols',
          item_type: 'protocol',
          original_table: PROTOCOLS_TABLE,
          original_id: protocol.id,
          item_title: protocol.title || 'Untitled Protocol',
          item_data: protocolRow,
          kind,
        });
        if (trashErr) {
          setListError(trashErr.message);
          return;
        }
      }
      await supabase.from(ITEMS_TABLE).delete().eq('protocol_id', protocol.id).eq('user_id', userId);
      await supabase.from(COMPLETIONS_TABLE).delete().eq('protocol_id', protocol.id).eq('user_id', userId);
      await supabase.from(SCHEDULES_TABLE).delete().eq('protocol_id', protocol.id).eq('user_id', userId);
      const { error: err } = await supabase
        .from(PROTOCOLS_TABLE)
        .delete()
        .eq('id', protocol.id)
        .eq('user_id', userId);
      if (err) setListError(err.message);
      if (selectedProtocolId === protocol.id) setSelectedProtocolId(null);
      await loadList();
    },
    [userId, selectedProtocolId, loadList]
  );

  const confirmDeleteProtocol = (protocol: ProtocolRow) => {
    Alert.alert(
      `Delete "${protocol.title}"?`,
      'This protocol will be moved to Trash (restorable from Trash on web). Its steps and completion log are permanently deleted and cannot be restored.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => void deleteProtocol(protocol, 'trash') },
      ]
    );
  };

  const archiveProtocol = (protocol: ProtocolRow) => {
    void deleteProtocol(protocol, 'archive');
  };

  const deleteGroup = useCallback(
    async (group: ProtocolGroupRow) => {
      if (!userId) return;
      const affected = protocols.filter((p) => p.group_id === group.id);
      await Promise.all(
        affected.map((p) =>
          supabase.from(PROTOCOLS_TABLE).update({ group_id: null }).eq('id', p.id).eq('user_id', userId)
        )
      );
      const { error: err } = await supabase
        .from(GROUPS_TABLE)
        .delete()
        .eq('id', group.id)
        .eq('user_id', userId);
      if (err) setListError(err.message);
      await loadList();
    },
    [userId, protocols, loadList]
  );

  const confirmDeleteGroup = (group: ProtocolGroupRow) => {
    Alert.alert(
      `Delete group "${group.name}"?`,
      'Protocols in this group will be moved to ungrouped. The group itself will be deleted.',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete Group', style: 'destructive', onPress: () => void deleteGroup(group) },
      ]
    );
  };

  const openProtocolMenu = (protocol: ProtocolRow) => {
    // Matches web's per-protocol dropdown (Edit / Archive / Delete) —
    // src/app/protocols/page.tsx renderProtocolItem DropdownMenuContent.
    Alert.alert(protocol.title, undefined, [
      { text: 'Edit', onPress: () => startEditProtocol(protocol) },
      { text: 'Archive', onPress: () => archiveProtocol(protocol) },
      { text: 'Delete', style: 'destructive', onPress: () => confirmDeleteProtocol(protocol) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const openGroupMenu = (group: ProtocolGroupRow) => {
    Alert.alert(group.name, undefined, [
      { text: 'Rename', onPress: () => startEditGroup(group) },
      { text: 'Delete Group', style: 'destructive', onPress: () => confirmDeleteGroup(group) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  const moveProtocol = async (protocol: ProtocolRow, direction: -1 | 1) => {
    if (!userId) return;
    const scopeGroupId = protocol.group_id ?? null;
    const scope = sortByOrder(protocols.filter((p) => (p.group_id ?? null) === scopeGroupId));
    const idx = scope.findIndex((p) => p.id === protocol.id);
    const newIdx = idx + direction;
    if (idx === -1 || newIdx < 0 || newIdx >= scope.length) return;
    const reordered = scope.slice();
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setProtocols((prev) =>
      prev.map((p) => {
        const i = reordered.findIndex((r) => r.id === p.id);
        return i === -1 ? p : { ...p, order: i };
      })
    );
    await Promise.all(
      reordered.map((p, i) => supabase.from(PROTOCOLS_TABLE).update({ order: i }).eq('id', p.id).eq('user_id', userId))
    );
  };

  const moveGroup = async (group: ProtocolGroupRow, direction: -1 | 1) => {
    if (!userId) return;
    const scope = sortByOrder(groups);
    const idx = scope.findIndex((g) => g.id === group.id);
    const newIdx = idx + direction;
    if (idx === -1 || newIdx < 0 || newIdx >= scope.length) return;
    const reordered = scope.slice();
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setGroups(reordered.map((g, i) => ({ ...g, order: i })));
    await Promise.all(
      reordered.map((g, i) => supabase.from(GROUPS_TABLE).update({ order: i }).eq('id', g.id).eq('user_id', userId))
    );
  };

  if (selectedProtocolId) {
    return (
      <ProtocolDetailScreen
        protocolId={selectedProtocolId}
        userId={userId as string}
        onBack={() => {
          setSelectedProtocolId(null);
          void loadList();
        }}
      />
    );
  }

  if (!userId) {
    return (
      <View style={styles.center}>
        <Text style={styles.emptyTitle}>Please sign in</Text>
        <Text style={styles.emptyHint}>Sign in to view and run your THEMIS protocols.</Text>
      </View>
    );
  }

  if (loadingList) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={t.goldBright} />
      </View>
    );
  }

  const ungroupedProtocols = sortByOrder(protocols.filter((p) => !p.group_id));
  const sortedGroups = sortByOrder(groups);

  return (
    <View style={styles.root}>
      <View style={styles.headerRow}>
        <Text style={styles.headerTitle}>THEMIS Protocols</Text>
        <Pressable style={styles.addButton} onPress={openAddChooser}>
          <Text style={styles.addButtonText}>+ Add</Text>
        </Pressable>
      </View>
      {listError ? <Text style={styles.error}>{listError}</Text> : null}

      <ScrollView style={styles.listScroll} contentContainerStyle={styles.listContent}>
        {protocols.length === 0 && groups.length === 0 ? (
          <Text style={styles.empty}>No protocols yet. Tap &quot;+ Add&quot; to create one.</Text>
        ) : null}

        {ungroupedProtocols.map((p, idx) => (
          <ProtocolRowView
            key={p.id}
            protocol={p}
            index={idx}
            count={ungroupedProtocols.length}
            onOpen={() => setSelectedProtocolId(p.id)}
            onMenu={() => openProtocolMenu(p)}
            onMoveUp={() => void moveProtocol(p, -1)}
            onMoveDown={() => void moveProtocol(p, 1)}
          />
        ))}

        {sortedGroups.map((g, gIdx) => {
          const groupProtocols = sortByOrder(protocols.filter((p) => p.group_id === g.id));
          const expanded = !!expandedGroups[g.id];
          return (
            <View key={g.id} style={styles.groupBlock}>
              <View style={styles.groupHeaderRow}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
                <Pressable style={styles.groupHeaderTouch} onPress={() => toggleGroupExpand(g.id)}>
                  <Text style={styles.groupChevron}>{expanded ? '▾' : '▸'}</Text>
                  <Text style={styles.groupName} numberOfLines={1}>
                    {g.name}
                  </Text>
                  <Text style={styles.groupCount}>({groupProtocols.length})</Text>
                </Pressable>
                <View style={styles.moveCol}>
                  <Pressable disabled={gIdx === 0} onPress={() => void moveGroup(g, -1)}>
                    <Text style={[styles.moveArrow, gIdx === 0 && styles.moveArrowDisabled]}>▲</Text>
                  </Pressable>
                  <Pressable disabled={gIdx === sortedGroups.length - 1} onPress={() => void moveGroup(g, 1)}>
                    <Text style={[styles.moveArrow, gIdx === sortedGroups.length - 1 && styles.moveArrowDisabled]}>
                      ▼
                    </Text>
                  </Pressable>
                </View>
                <Pressable style={styles.rowMenuButton} onPress={() => openGroupMenu(g)}>
                  <Text style={styles.rowMenuText}>⋯</Text>
                </Pressable>
              </View>
              {expanded ? (
                groupProtocols.length === 0 ? (
                  <Text style={styles.emptyGroupText}>No protocols in this group.</Text>
                ) : (
                  groupProtocols.map((p, idx) => (
                    <ProtocolRowView
                      key={p.id}
                      protocol={p}
                      index={idx}
                      count={groupProtocols.length}
                      onOpen={() => setSelectedProtocolId(p.id)}
                      onMenu={() => openProtocolMenu(p)}
                      onMoveUp={() => void moveProtocol(p, -1)}
                      onMoveDown={() => void moveProtocol(p, 1)}
                    />
                  ))
                )
              ) : null}
            </View>
          );
        })}
      </ScrollView>

      {/* Add Protocol/Group modal */}
      <Modal visible={addMode !== null} transparent animationType="fade" onRequestClose={() => setAddMode(null)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setAddMode(null)}>
          <Pressable style={styles.formCard} onPress={(e) => e.stopPropagation()}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <Text style={styles.formTitle}>{addMode === 'protocol' ? 'Add New Protocol' : 'Add New Group'}</Text>
            <TextInput
              style={styles.formInput}
              placeholder={addMode === 'protocol' ? 'Protocol name' : 'Group name'}
              placeholderTextColor={c.slate}
              value={addName}
              onChangeText={setAddName}
              onSubmitEditing={() => void submitAdd()}
              returnKeyType="done"
              autoFocus
            />
            {addMode === 'protocol' && groups.length > 0 ? (
              <>
                <Text style={styles.formLabel}>Group (optional)</Text>
                <View style={styles.chipRow}>
                  <Chip text="No group" active={addGroupId === null} onPress={() => setAddGroupId(null)} />
                  {groups.map((g) => (
                    <Chip key={g.id} text={g.name} active={addGroupId === g.id} onPress={() => setAddGroupId(g.id)} />
                  ))}
                </View>
              </>
            ) : null}
            <View style={styles.formActions}>
              <Pressable style={styles.formCancel} onPress={() => setAddMode(null)}>
                <Text style={styles.formCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.formSubmit, !addName.trim() && styles.formSubmitDisabled]}
                disabled={!addName.trim() || busy}
                onPress={() => void submitAdd()}
              >
                <Text style={styles.formSubmitText}>{addMode === 'protocol' ? 'Add Protocol' : 'Add Group'}</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Edit Protocol modal */}
      <Modal
        visible={!!editingProtocol}
        transparent
        animationType="fade"
        onRequestClose={() => setEditingProtocol(null)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setEditingProtocol(null)}>
          <Pressable style={styles.formCard} onPress={(e) => e.stopPropagation()}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <Text style={styles.formTitle}>Edit Protocol</Text>
            <TextInput
              style={styles.formInput}
              value={editName}
              onChangeText={setEditName}
              onSubmitEditing={() => void submitEditProtocol()}
              returnKeyType="done"
              autoFocus
            />
            {groups.length > 0 ? (
              <>
                <Text style={styles.formLabel}>Group</Text>
                <View style={styles.chipRow}>
                  <Chip text="No group" active={editGroupId === null} onPress={() => setEditGroupId(null)} />
                  {groups.map((g) => (
                    <Chip key={g.id} text={g.name} active={editGroupId === g.id} onPress={() => setEditGroupId(g.id)} />
                  ))}
                </View>
              </>
            ) : null}
            <View style={styles.formActions}>
              <Pressable style={styles.formCancel} onPress={() => setEditingProtocol(null)}>
                <Text style={styles.formCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.formSubmit, !editName.trim() && styles.formSubmitDisabled]}
                disabled={!editName.trim()}
                onPress={() => void submitEditProtocol()}
              >
                <Text style={styles.formSubmitText}>Save</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Rename Group modal */}
      <Modal visible={!!editingGroup} transparent animationType="fade" onRequestClose={() => setEditingGroup(null)}>
        <Pressable style={styles.modalBackdrop} onPress={() => setEditingGroup(null)}>
          <Pressable style={styles.formCard} onPress={(e) => e.stopPropagation()}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <Text style={styles.formTitle}>Rename Group</Text>
            <TextInput
              style={styles.formInput}
              value={editGroupName}
              onChangeText={setEditGroupName}
              onSubmitEditing={() => void submitEditGroup()}
              returnKeyType="done"
              autoFocus
            />
            <View style={styles.formActions}>
              <Pressable style={styles.formCancel} onPress={() => setEditingGroup(null)}>
                <Text style={styles.formCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.formSubmit, !editGroupName.trim() && styles.formSubmitDisabled]}
                disabled={!editGroupName.trim()}
                onPress={() => void submitEditGroup()}
              >
                <Text style={styles.formSubmitText}>Save</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function ProtocolRowView({
  protocol,
  index,
  count,
  onOpen,
  onMenu,
  onMoveUp,
  onMoveDown,
}: {
  protocol: ProtocolRow;
  index: number;
  count: number;
  onOpen: () => void;
  onMenu: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  return (
    <View style={styles.protocolRow}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <View style={styles.moveCol}>
        <Pressable disabled={index === 0} onPress={onMoveUp}>
          <Text style={[styles.moveArrow, index === 0 && styles.moveArrowDisabled]}>▲</Text>
        </Pressable>
        <Pressable disabled={index === count - 1} onPress={onMoveDown}>
          <Text style={[styles.moveArrow, index === count - 1 && styles.moveArrowDisabled]}>▼</Text>
        </Pressable>
      </View>
      <Pressable style={styles.protocolTouch} onPress={onOpen}>
        <Text style={styles.protocolTitle} numberOfLines={1}>
          {protocol.title}
        </Text>
        <Text style={styles.chevronRight}>›</Text>
      </Pressable>
      <Pressable style={styles.rowMenuButton} onPress={onMenu}>
        <Text style={styles.rowMenuText}>⋯</Text>
      </Pressable>
    </View>
  );
}

// ===== Protocol detail + runner =====

// Exported so METIS can embed the full protocol runner inline (THEMIS tab on
// an agenda item). Self-contained: it owns all its own loading/runner state
// and only needs a protocolId, the userId, and an onBack handler.
export function ProtocolDetailScreen({
  protocolId,
  userId,
  onBack,
}: {
  protocolId: string;
  userId: string;
  onBack: () => void;
}) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const [protocol, setProtocol] = useState<ProtocolRow | null>(null);
  const protocolRef = useRef<ProtocolRow | null>(null);
  useEffect(() => { protocolRef.current = protocol; }, [protocol]);
  const [steps, setSteps] = useState<ProtocolItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [newStepTitle, setNewStepTitle] = useState('');
  const [newStepDuration, setNewStepDuration] = useState('');

  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showNotesSheet, setShowNotesSheet] = useState(false);
  const [notesValue, setNotesValue] = useState('');

  const [showLogSheet, setShowLogSheet] = useState(false);
  const [logs, setLogs] = useState<ProtocolCompletionRow[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  const [showSettingsSheet, setShowSettingsSheet] = useState(false);
  const [transitionEnabled, setTransitionEnabled] = useState(false);
  const [transitionSeconds, setTransitionSeconds] = useState('60');

  const [stepNotesStepId, setStepNotesStepId] = useState<string | null>(null);
  const [stepNotesValue, setStepNotesValue] = useState('');

  // Runner state
  const [isRunning, setIsRunning] = useState(false);
  const [activeStepId, setActiveStepId] = useState<string | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [transitionCountdown, setTransitionCountdown] = useState<number | null>(null);
  const [showTimerDialog, setShowTimerDialog] = useState(false);
  const [expiredStepId, setExpiredStepId] = useState<string | null>(null);

  const stepsRef = useRef(steps);
  useEffect(() => {
    stepsRef.current = steps;
  }, [steps]);
  const activeStepIdRef = useRef(activeStepId);
  useEffect(() => {
    activeStepIdRef.current = activeStepId;
  }, [activeStepId]);
  const expiredStepIdRef = useRef(expiredStepId);
  useEffect(() => {
    expiredStepIdRef.current = expiredStepId;
  }, [expiredStepId]);
  const transitionEnabledRef = useRef(transitionEnabled);
  useEffect(() => {
    transitionEnabledRef.current = transitionEnabled;
  }, [transitionEnabled]);
  const transitionSecondsRef = useRef(transitionSeconds);
  useEffect(() => {
    transitionSecondsRef.current = transitionSeconds;
  }, [transitionSeconds]);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transitionIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const logIdRef = useRef<string | null>(null);
  const sessionStartRef = useRef<number | null>(null);
  const stepsCompletedRef = useRef(0);
  const stepsRepeatedRef = useRef<Record<string, number>>({});
  const pauseCountRef = useRef(0);

  // Timer audio (parity note in file header): looping alarm sound object,
  // matching web's <audio loop> element (playSound/stopSound).
  const alarmSoundRef = useRef<Audio.Sound | null>(null);

  // Backgrounded/killed-app alert: id of the local notification scheduled
  // against the active step's wall-clock expiry (see armStepDeadline).
  const scheduledNotificationIdRef = useRef<string | null>(null);
  const cancelScheduledNotif = useCallback(() => {
    const id = scheduledNotificationIdRef.current;
    scheduledNotificationIdRef.current = null;
    void cancelTimerNotification(id);
  }, []);
  const armStepDeadline = useCallback(
    (remainingSecondsAtArmTime: number) => {
      cancelScheduledNotif();
      const deadline = Date.now() + remainingSecondsAtArmTime * 1000;
      const title = `${protocolRef.current?.title || 'Protocol'} — step timer done`;
      void scheduleTimerNotification(deadline, title, 'Step timer complete.').then((id) => {
        scheduledNotificationIdRef.current = id;
      });
    },
    [cancelScheduledNotif]
  );

  // Schedule sheet state — mirrors web's showScheduleSheet + schedule doc.
  const [showScheduleSheet, setShowScheduleSheet] = useState(false);
  const [schedule, setSchedule] = useState<ProtocolScheduleRow | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);

  const loadProtocol = useCallback(async () => {
    const { data, error: err } = await supabase
      .from(PROTOCOLS_TABLE)
      .select('id, title, group_id, order, notes, transitionEnabled, transitionSeconds')
      .eq('id', protocolId)
      .eq('user_id', userId)
      .maybeSingle();
    if (err) {
      setError(err.message);
      return;
    }
    if (data) {
      const row = data as ProtocolRow;
      setProtocol(row);
      setNotesValue(row.notes ?? '');
      setTransitionEnabled(!!row.transitionEnabled);
      setTransitionSeconds(String(row.transitionSeconds ?? 60));
    }
  }, [protocolId, userId]);

  const loadSteps = useCallback(async () => {
    const { data, error: err } = await supabase
      .from(ITEMS_TABLE)
      .select('id, protocol_id, title, done, duration_minutes, order, notes')
      .eq('protocol_id', protocolId)
      .eq('user_id', userId)
      .order('order', { ascending: true });
    if (err) {
      setError(err.message);
      return;
    }
    setSteps((data ?? []) as ProtocolItemRow[]);
  }, [protocolId, userId]);

  useEffect(() => {
    setLoading(true);
    void Promise.all([loadProtocol(), loadSteps()]).finally(() => setLoading(false));
  }, [loadProtocol, loadSteps]);

  // Allow the alarm to play even if the phone's silent switch is on, matching
  // web's audio cue firing regardless of OS-level "silent" mode.
  useEffect(() => {
    void Audio.setAudioModeAsync({ playsInSilentModeIOS: true });
  }, []);

  // Stop any playing alarm/vibration if this screen unmounts mid-run (e.g.
  // switching OLMPS tools while a step's timer has expired).
  useEffect(() => {
    return () => {
      Vibration.cancel();
      const sound = alarmSoundRef.current;
      alarmSoundRef.current = null;
      if (sound) {
        void sound.unloadAsync();
      }
    };
  }, []);

  const markStepDone = useCallback(
    async (stepId: string) => {
      setSteps((prev) => prev.map((s) => (s.id === stepId ? { ...s, done: true } : s)));
      await supabase.from(ITEMS_TABLE).update({ done: true }).eq('id', stepId).eq('user_id', userId);
    },
    [userId]
  );

  const finalizeLog = useCallback(
    async (status: 'success' | 'abandoned') => {
      if (!logIdRef.current || sessionStartRef.current === null) return;
      const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      await supabase
        .from(COMPLETIONS_TABLE)
        .update({
          completed_at: new Date().toISOString(),
          status,
          steps_completed: stepsCompletedRef.current,
          steps_repeated: stepsRepeatedRef.current,
          total_duration_seconds: elapsed,
          pause_count: pauseCountRef.current,
        })
        .eq('id', logIdRef.current)
        .eq('user_id', userId);
      logIdRef.current = null;
    },
    [userId]
  );

  const advanceToNextStep = useCallback(
    (skipStepId?: string) => {
      const sorted = sortByOrder(stepsRef.current);
      const nextStep = sorted.find((s) => !s.done && s.id !== skipStepId);
      if (nextStep) {
        const secs = Math.round((nextStep.duration_minutes || 1) * 60);
        setActiveStepId(nextStep.id);
        setRemainingSeconds(secs);
        setIsRunning(true);
        armStepDeadline(secs);
      } else {
        setIsRunning(false);
        setActiveStepId(null);
        cancelScheduledNotif();
        void finalizeLog('success');
      }
    },
    [finalizeLog, armStepDeadline, cancelScheduledNotif]
  );

  const clearTransitionTimer = useCallback(() => {
    if (transitionIntervalRef.current) {
      clearInterval(transitionIntervalRef.current);
      transitionIntervalRef.current = null;
    }
    setTransitionCountdown(null);
  }, []);

  // Timer audio: native stand-in for web's playSound/stopSound (looping
  // alarm, transition OFF) and playTransitionBell (one-shot chime,
  // transition ON) — see parity notes in file header. Vibration is an
  // always-fired fallback/companion cue in both cases.
  const stopAlarm = useCallback(async () => {
    const sound = alarmSoundRef.current;
    alarmSoundRef.current = null;
    if (sound) {
      try {
        await sound.stopAsync();
        await sound.unloadAsync();
      } catch {
        // best-effort cleanup
      }
    }
  }, []);

  const playAlarm = useCallback(async () => {
    await stopAlarm();
    Vibration.vibrate([0, 400, 200, 400], true);
    try {
      const { sound } = await Audio.Sound.createAsync({ uri: BEEP_WAV_DATA_URI }, { isLooping: true, volume: 1 });
      alarmSoundRef.current = sound;
      await sound.playAsync();
    } catch {
      // audio unavailable (e.g. silent hardware failure) — vibration still fires.
    }
  }, [stopAlarm]);

  const playTransitionBell = useCallback(async () => {
    Vibration.vibrate(200);
    try {
      const { sound } = await Audio.Sound.createAsync({ uri: BEEP_WAV_DATA_URI }, { isLooping: false, volume: 1 });
      sound.setOnPlaybackStatusUpdate((status) => {
        if (status.isLoaded && status.didJustFinish) void sound.unloadAsync();
      });
      await sound.playAsync();
    } catch {
      // vibration still fires even if audio fails.
    }
  }, []);

  const handleTimerExpired = useCallback(() => {
    setIsRunning(false);
    // In-app expiry handler is running (app foregrounded) — cancel the
    // OS-scheduled notification for this same step deadline before playing
    // the in-app alarm/vibration, so the two never fire together.
    cancelScheduledNotif();
    const currentActive = activeStepIdRef.current;
    setExpiredStepId(currentActive);
    setShowTimerDialog(true);

    if (transitionEnabledRef.current) {
      void playTransitionBell();
      const secs = Number(transitionSecondsRef.current) || 60;
      setTransitionCountdown(secs);
      transitionIntervalRef.current = setInterval(() => {
        setTransitionCountdown((prev) => {
          if (prev === null || prev <= 1) {
            if (transitionIntervalRef.current) {
              clearInterval(transitionIntervalRef.current);
              transitionIntervalRef.current = null;
            }
            setShowTimerDialog(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      void playAlarm();
    }
  }, [playAlarm, playTransitionBell, cancelScheduledNotif]);

  // Timer tick
  useEffect(() => {
    if (!isRunning || !activeStepId) return;
    intervalRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          handleTimerExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, activeStepId, handleTimerExpired]);

  // Auto-advance when the transition countdown reaches 0
  useEffect(() => {
    if (transitionCountdown === 0 && expiredStepIdRef.current) {
      const stepId = expiredStepIdRef.current;
      setExpiredStepId(null);
      void (async () => {
        await markStepDone(stepId);
        stepsCompletedRef.current += 1;
        advanceToNextStep(stepId);
        setTransitionCountdown(null);
      })();
    }
  }, [transitionCountdown, markStepDone, advanceToNextStep]);

  // Best-effort finalize on unmount: if this screen unmounts while a run is
  // still active (e.g. switching OLMPS tools without pressing Back), don't
  // leave the protocol_completions row inserted in startProtocol orphaned as
  // status 'abandoned' with completed_at null forever.
  useEffect(() => {
    return () => {
      if (logIdRef.current && sessionStartRef.current !== null) {
        const logId = logIdRef.current;
        const elapsed = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        void supabase
          .from(COMPLETIONS_TABLE)
          .update({
            completed_at: new Date().toISOString(),
            status: 'abandoned',
            steps_completed: stepsCompletedRef.current,
            steps_repeated: stepsRepeatedRef.current,
            total_duration_seconds: elapsed,
            pause_count: pauseCountRef.current,
          })
          .eq('id', logId)
          .eq('user_id', userId);
      }
    };
  }, [userId]);

  const startProtocol = useCallback(async () => {
    if (!protocol) return;
    let workingSteps = sortByOrder(stepsRef.current);
    if (workingSteps.length === 0) return;
    const firstUnchecked = workingSteps.find((s) => !s.done);
    if (!firstUnchecked) {
      await Promise.all(
        workingSteps.map((s) => supabase.from(ITEMS_TABLE).update({ done: false }).eq('id', s.id).eq('user_id', userId))
      );
      workingSteps = workingSteps.map((s) => ({ ...s, done: false }));
      setSteps(workingSteps);
    }
    const startStep = workingSteps.find((s) => !s.done) || workingSteps[0];

    sessionStartRef.current = Date.now();
    stepsCompletedRef.current = 0;
    stepsRepeatedRef.current = {};
    pauseCountRef.current = 0;

    const { data: logData, error: logErr } = await supabase
      .from(COMPLETIONS_TABLE)
      .insert({
        user_id: userId,
        protocol_id: protocolId,
        protocol_title: protocol.title || '',
        started_at: new Date().toISOString(),
        completed_at: null,
        status: 'abandoned',
        steps_completed: 0,
        steps_repeated: {},
        total_duration_seconds: 0,
        pause_count: 0,
      })
      .select()
      .single();
    if (logErr) setError(logErr.message);
    if (logData) logIdRef.current = (logData as { id: string }).id;

    const startSecs = Math.round((startStep.duration_minutes || 1) * 60);
    setActiveStepId(startStep.id);
    setRemainingSeconds(startSecs);
    setIsRunning(true);
    armStepDeadline(startSecs);
  }, [protocol, protocolId, userId, armStepDeadline]);

  const pause = useCallback(async () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsRunning(false);
    cancelScheduledNotif();
    pauseCountRef.current += 1;
    if (logIdRef.current) {
      await supabase
        .from(COMPLETIONS_TABLE)
        .update({ pause_count: pauseCountRef.current })
        .eq('id', logIdRef.current)
        .eq('user_id', userId);
    }
  }, [userId, cancelScheduledNotif]);

  const remainingSecondsRef = useRef(remainingSeconds);
  useEffect(() => { remainingSecondsRef.current = remainingSeconds; }, [remainingSeconds]);

  const handlePlayPause = useCallback(async () => {
    if (activeStepId === null) {
      await startProtocol();
      return;
    }
    if (isRunning) {
      await pause();
    } else {
      setIsRunning(true);
      armStepDeadline(remainingSecondsRef.current);
    }
  }, [activeStepId, isRunning, startProtocol, pause, armStepDeadline]);

  const handleNext = useCallback(async () => {
    Vibration.cancel();
    await stopAlarm();
    clearTransitionTimer();
    setShowTimerDialog(false);
    const stepToAdvancePast = expiredStepIdRef.current ?? activeStepIdRef.current;
    if (expiredStepIdRef.current) {
      await markStepDone(expiredStepIdRef.current);
      stepsCompletedRef.current += 1;
    }
    setExpiredStepId(null);
    advanceToNextStep(stepToAdvancePast ?? undefined);
  }, [stopAlarm, clearTransitionTimer, markStepDone, advanceToNextStep]);

  const handleRepeat = useCallback(() => {
    Vibration.cancel();
    void stopAlarm();
    clearTransitionTimer();
    setShowTimerDialog(false);
    const stepId = expiredStepIdRef.current ?? activeStepIdRef.current;
    if (stepId) {
      stepsRepeatedRef.current[stepId] = (stepsRepeatedRef.current[stepId] || 0) + 1;
      const step = stepsRef.current.find((s) => s.id === stepId);
      const secs = Math.round((step?.duration_minutes || 1) * 60);
      setRemainingSeconds(secs);
      setActiveStepId(stepId);
      setIsRunning(true);
      armStepDeadline(secs);
    }
    setExpiredStepId(null);
  }, [stopAlarm, clearTransitionTimer, armStepDeadline]);

  const performAbandon = useCallback(async () => {
    Vibration.cancel();
    await stopAlarm();
    clearTransitionTimer();
    cancelScheduledNotif();
    if (intervalRef.current) clearInterval(intervalRef.current);
    setShowTimerDialog(false);
    setExpiredStepId(null);
    await finalizeLog('abandoned');
    setIsRunning(false);
    setActiveStepId(null);
    setRemainingSeconds(0);
  }, [stopAlarm, clearTransitionTimer, finalizeLog, cancelScheduledNotif]);

  const handleAbandon = useCallback(() => {
    Alert.alert('Abandon protocol?', 'This ends the current run and logs it as abandoned.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Abandon', style: 'destructive', onPress: () => void performAbandon() },
    ]);
  }, [performAbandon]);

  const handleBackPress = useCallback(() => {
    if (isRunning || activeStepId) {
      Alert.alert('Leave protocol?', 'Protocol running — leaving will abandon this run.', [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Abandon & Leave',
          style: 'destructive',
          onPress: () => {
            void (async () => {
              await performAbandon();
              onBack();
            })();
          },
        },
      ]);
      return;
    }
    onBack();
  }, [isRunning, activeStepId, performAbandon, onBack]);

  const handleToggleDone = useCallback(
    async (step: ProtocolItemRow) => {
      const nextDone = !step.done;
      setSteps((prev) => prev.map((s) => (s.id === step.id ? { ...s, done: nextDone } : s)));
      const { error: err } = await supabase
        .from(ITEMS_TABLE)
        .update({ done: nextDone })
        .eq('id', step.id)
        .eq('user_id', userId);
      if (err) setError(err.message);
      if (nextDone && isRunning && step.id === activeStepId) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        stepsCompletedRef.current += 1;
        advanceToNextStep(step.id);
      }
    },
    [userId, isRunning, activeStepId, advanceToNextStep]
  );

  const handleAllComplete = useCallback(async () => {
    const currentSteps = stepsRef.current;
    const undone = currentSteps.filter((s) => !s.done);
    await Promise.all(
      undone.map((s) => supabase.from(ITEMS_TABLE).update({ done: true }).eq('id', s.id).eq('user_id', userId))
    );
    setSteps((prev) => prev.map((s) => ({ ...s, done: true })));
    if (intervalRef.current) clearInterval(intervalRef.current);
    cancelScheduledNotif();
    setIsRunning(false);
    setActiveStepId(null);
    stepsCompletedRef.current += undone.length;
    if (logIdRef.current) {
      await finalizeLog('success');
    }
  }, [userId, finalizeLog, cancelScheduledNotif]);

  const handleResetAllSteps = useCallback(async () => {
    setShowActionMenu(false);
    const currentSteps = stepsRef.current;
    const done = currentSteps.filter((s) => s.done);
    await Promise.all(
      done.map((s) => supabase.from(ITEMS_TABLE).update({ done: false }).eq('id', s.id).eq('user_id', userId))
    );
    setSteps((prev) => prev.map((s) => ({ ...s, done: false })));
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsRunning(false);
    setActiveStepId(null);
    setRemainingSeconds(0);
    logIdRef.current = null;
  }, [userId]);

  const handleAddStep = async () => {
    if (!newStepTitle.trim() || busy) return;
    setBusy(true);
    const nextOrder = steps.length > 0 ? Math.max(...steps.map((s) => s.order ?? 0)) + 1 : 0;
    const { error: err } = await supabase.from(ITEMS_TABLE).insert({
      user_id: userId,
      protocol_id: protocolId,
      title: newStepTitle.trim(),
      done: false,
      duration_minutes: parseFloat(newStepDuration) || 1,
      order: nextOrder,
      created_at: new Date().toISOString(),
    });
    if (err) setError(err.message);
    setNewStepTitle('');
    setNewStepDuration('');
    await loadSteps();
    setBusy(false);
  };

  const handleDeleteStep = (step: ProtocolItemRow) => {
    Alert.alert('Delete this step?', step.title, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setSteps((prev) => prev.filter((s) => s.id !== step.id));
          if (activeStepId === step.id) {
            if (intervalRef.current) clearInterval(intervalRef.current);
            setIsRunning(false);
            setActiveStepId(null);
          }
          const { error: err } = await supabase.from(ITEMS_TABLE).delete().eq('id', step.id).eq('user_id', userId);
          if (err) {
            setError(err.message);
            await loadSteps();
          }
        },
      },
    ]);
  };

  const handleUpdateStepTitle = async (step: ProtocolItemRow, title: string) => {
    if (!title.trim() || title === step.title) return;
    setSteps((prev) => prev.map((s) => (s.id === step.id ? { ...s, title: title.trim() } : s)));
    await supabase.from(ITEMS_TABLE).update({ title: title.trim() }).eq('id', step.id).eq('user_id', userId);
  };

  const handleUpdateStepDuration = async (step: ProtocolItemRow, text: string) => {
    const val = parseFloat(text) || 0;
    if (val === step.duration_minutes) return;
    setSteps((prev) => prev.map((s) => (s.id === step.id ? { ...s, duration_minutes: val } : s)));
    await supabase.from(ITEMS_TABLE).update({ duration_minutes: val }).eq('id', step.id).eq('user_id', userId);
  };

  const moveStep = async (step: ProtocolItemRow, direction: -1 | 1) => {
    const sorted = sortByOrder(steps);
    const idx = sorted.findIndex((s) => s.id === step.id);
    const newIdx = idx + direction;
    if (idx === -1 || newIdx < 0 || newIdx >= sorted.length) return;
    const reordered = sorted.slice();
    const [moved] = reordered.splice(idx, 1);
    reordered.splice(newIdx, 0, moved);
    setSteps(reordered.map((s, i) => ({ ...s, order: i })));
    await Promise.all(
      reordered.map((s, i) => supabase.from(ITEMS_TABLE).update({ order: i }).eq('id', s.id).eq('user_id', userId))
    );
  };

  const saveNotes = async () => {
    await supabase.from(PROTOCOLS_TABLE).update({ notes: notesValue }).eq('id', protocolId).eq('user_id', userId);
    setProtocol((prev) => (prev ? { ...prev, notes: notesValue } : prev));
    setShowNotesSheet(false);
  };

  const fetchLogs = async () => {
    setLogsLoading(true);
    const { data, error: err } = await supabase
      .from(COMPLETIONS_TABLE)
      .select('id, protocol_id, protocol_title, started_at, completed_at, status, steps_completed, steps_repeated, total_duration_seconds, pause_count')
      .eq('protocol_id', protocolId)
      .eq('user_id', userId)
      .order('started_at', { ascending: false });
    if (err) setError(err.message);
    else setLogs((data ?? []) as ProtocolCompletionRow[]);
    setLogsLoading(false);
  };

  const openLogSheet = () => {
    setShowActionMenu(false);
    setShowLogSheet(true);
    void fetchLogs();
  };

  const toggleTransition = async (checked: boolean) => {
    setTransitionEnabled(checked);
    await supabase.from(PROTOCOLS_TABLE).update({ transitionEnabled: checked }).eq('id', protocolId).eq('user_id', userId);
  };

  const commitTransitionSeconds = async (text: string) => {
    const val = parseInt(text, 10) || 60;
    setTransitionSeconds(String(val));
    await supabase
      .from(PROTOCOLS_TABLE)
      .update({ transitionSeconds: val })
      .eq('id', protocolId)
      .eq('user_id', userId);
  };

  const saveStepNotes = async () => {
    if (!stepNotesStepId) return;
    await supabase.from(ITEMS_TABLE).update({ notes: stepNotesValue }).eq('id', stepNotesStepId).eq('user_id', userId);
    setSteps((prev) => prev.map((s) => (s.id === stepNotesStepId ? { ...s, notes: stepNotesValue } : s)));
    setStepNotesStepId(null);
  };

  // Schedule handlers — same table/columns and instance shape as web
  // (src/app/protocols/[protocolId]/page.tsx: PROTOCOL_SCHEDULES_TABLE row
  // { id, user_id, protocol_id, instances }, one row per protocol).
  const fetchSchedule = useCallback(async () => {
    setScheduleLoading(true);
    const { data, error: err } = await supabase
      .from(SCHEDULES_TABLE)
      .select('id, user_id, protocol_id, instances')
      .eq('protocol_id', protocolId)
      .eq('user_id', userId)
      .maybeSingle();
    if (err) setError(err.message);
    else setSchedule((data as ProtocolScheduleRow | null) ?? null);
    setScheduleLoading(false);
  }, [protocolId, userId]);

  const openScheduleSheet = () => {
    setShowActionMenu(false);
    setShowScheduleSheet(true);
    void fetchSchedule();
  };

  const saveScheduleInstances = useCallback(
    async (newInstances: ProtocolScheduleInstance[]) => {
      setSchedule((prev) => (prev ? { ...prev, instances: newInstances } : prev));
      if (schedule?.id) {
        await supabase.from(SCHEDULES_TABLE).update({ instances: newInstances }).eq('id', schedule.id).eq('user_id', userId);
      } else {
        const { data, error: err } = await supabase
          .from(SCHEDULES_TABLE)
          .insert({ user_id: userId, protocol_id: protocolId, instances: newInstances })
          .select()
          .single();
        if (err) setError(err.message);
        if (data) setSchedule(data as ProtocolScheduleRow);
      }
    },
    [schedule?.id, protocolId, userId]
  );

  const handleAddScheduleInstance = () => {
    const newInstance: ProtocolScheduleInstance = { id: `inst-${Date.now()}`, time: '09:00', days: [] };
    void saveScheduleInstances([...(schedule?.instances ?? []), newInstance]);
  };

  const handleUpdateScheduleInstanceTime = (instanceId: string, time: string) => {
    const current = schedule?.instances ?? [];
    void saveScheduleInstances(current.map((inst) => (inst.id === instanceId ? { ...inst, time } : inst)));
  };

  const handleToggleScheduleDay = (instanceId: string, day: string) => {
    const current = schedule?.instances ?? [];
    const updated = current.map((inst) => {
      if (inst.id !== instanceId) return inst;
      const days = inst.days.includes(day) ? inst.days.filter((d) => d !== day) : [...inst.days, day];
      return { ...inst, days };
    });
    void saveScheduleInstances(updated);
  };

  const handleDeleteScheduleInstance = (instanceId: string) => {
    const current = schedule?.instances ?? [];
    void saveScheduleInstances(current.filter((inst) => inst.id !== instanceId));
  };

  const confirmDeleteScheduleInstance = (instanceId: string) => {
    Alert.alert('Delete this schedule instance?', 'This permanently removes the scheduled instance.', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: () => handleDeleteScheduleInstance(instanceId) },
    ]);
  };

  if (loading) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={t.goldBright} />
      </View>
    );
  }

  const sortedSteps = sortByOrder(steps);
  const allDone = sortedSteps.length > 0 && sortedSteps.every((s) => s.done);
  const activeStepTitle = sortedSteps.find((s) => s.id === expiredStepId)?.title ?? '';

  return (
    <View style={styles.root}>
      <View style={styles.detailHeader}>
        <Pressable onPress={handleBackPress} style={styles.backButton}>
          <Text style={styles.backText}>‹ Back</Text>
        </Pressable>
        <Text style={styles.detailTitle} numberOfLines={1}>
          {protocol?.title ?? 'Protocol'}
        </Text>
        <Pressable onPress={() => setShowActionMenu(true)} style={styles.menuButton}>
          <Text style={styles.rowMenuText}>⋯</Text>
        </Pressable>
      </View>

      {error ? <Text style={styles.error}>{error}</Text> : null}

      <View style={styles.controlsRow}>
        {isRunning || activeStepId ? <Text style={styles.timerBadge}>{formatTimer(remainingSeconds)}</Text> : null}
        <Pressable
          style={[styles.primaryButton, isRunning && styles.secondaryButton]}
          onPress={() => void handlePlayPause()}
          disabled={sortedSteps.length === 0}
        >
          <Text style={[styles.primaryButtonText, isRunning && styles.secondaryButtonText]}>
            {isRunning ? 'Pause' : activeStepId ? 'Resume' : allDone ? 'Restart' : 'Play'}
          </Text>
        </Pressable>
        <Pressable style={styles.outlineButton} onPress={() => void handleAllComplete()}>
          <Text style={styles.outlineButtonText}>All Complete</Text>
        </Pressable>
      </View>

      {activeStepId ? (
        <View style={styles.runnerControlsRow}>
          <Pressable style={styles.smallButton} onPress={() => void handleNext()}>
            <Text style={styles.smallButtonText}>Next</Text>
          </Pressable>
          <Pressable style={styles.smallButton} onPress={handleRepeat}>
            <Text style={styles.smallButtonText}>Repeat</Text>
          </Pressable>
          <Pressable style={styles.smallDangerButton} onPress={handleAbandon}>
            <Text style={styles.smallDangerButtonText}>Abandon</Text>
          </Pressable>
        </View>
      ) : null}

      {transitionEnabled && transitionCountdown !== null && transitionCountdown > 0 ? (
        <Text style={styles.transitionHint}>Auto-advancing in {formatTimer(transitionCountdown)}</Text>
      ) : null}

      <ScrollView style={styles.stepsScroll} contentContainerStyle={styles.stepsContent}>
        {sortedSteps.length === 0 ? <Text style={styles.empty}>No steps yet. Add one below.</Text> : null}
        {sortedSteps.map((step, idx) => (
          <StepRow
            key={step.id}
            step={step}
            index={idx}
            count={sortedSteps.length}
            isActive={step.id === activeStepId}
            onToggleDone={() => void handleToggleDone(step)}
            onMoveUp={() => void moveStep(step, -1)}
            onMoveDown={() => void moveStep(step, 1)}
            onTitleChange={(t) => void handleUpdateStepTitle(step, t)}
            onDurationChange={(t) => void handleUpdateStepDuration(step, t)}
            onOpenNotes={() => {
              setStepNotesStepId(step.id);
              setStepNotesValue(step.notes ?? '');
            }}
            onDelete={() => handleDeleteStep(step)}
          />
        ))}

        <View style={styles.addStepRow}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
          <TextInput
            style={styles.addStepTitleInput}
            placeholder="Add a step…"
            placeholderTextColor={c.slate}
            value={newStepTitle}
            onChangeText={setNewStepTitle}
            onSubmitEditing={() => void handleAddStep()}
            returnKeyType="done"
          />
          <TextInput
            style={styles.addStepDurationInput}
            placeholder="min"
            placeholderTextColor={c.slate}
            keyboardType="numeric"
            value={newStepDuration}
            onChangeText={setNewStepDuration}
            onSubmitEditing={() => void handleAddStep()}
            returnKeyType="done"
          />
          <Pressable
            style={styles.addStepButton}
            onPress={() => void handleAddStep()}
            disabled={!newStepTitle.trim() || busy}
          >
            <Text style={styles.addStepButtonText}>Add</Text>
          </Pressable>
        </View>
      </ScrollView>

      {/* Action menu: Notes / Log / Settings / Reset All Steps */}
      <Modal
        visible={showActionMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowActionMenu(false)}
      >
        <Pressable style={styles.modalBackdrop} onPress={() => setShowActionMenu(false)}>
          <Pressable style={styles.menuCard} onPress={(e) => e.stopPropagation()}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <Pressable
              style={styles.menuItem}
              onPress={() => {
                setShowActionMenu(false);
                setShowNotesSheet(true);
              }}
            >
              <Text style={styles.menuItemText}>Notes</Text>
            </Pressable>
            <Pressable style={styles.menuItem} onPress={openLogSheet}>
              <Text style={styles.menuItemText}>Log</Text>
            </Pressable>
            <Pressable style={styles.menuItem} onPress={openScheduleSheet}>
              <Text style={styles.menuItemText}>Schedule</Text>
            </Pressable>
            <Pressable
              style={styles.menuItem}
              onPress={() => {
                setShowActionMenu(false);
                setShowSettingsSheet(true);
              }}
            >
              <Text style={styles.menuItemText}>Settings</Text>
            </Pressable>
            <Pressable style={styles.menuItem} onPress={() => void handleResetAllSteps()}>
              <Text style={[styles.menuItemText, styles.menuItemDanger]}>Reset All Steps</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Notes sheet */}
      <Modal
        visible={showNotesSheet}
        transparent
        animationType="slide"
        onRequestClose={() => setShowNotesSheet(false)}
      >
        <View style={styles.sheetBackdrop}>
          <View style={styles.sheet}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Notes</Text>
              <Pressable onPress={() => setShowNotesSheet(false)} style={styles.sheetClose}>
                <Text style={styles.sheetCloseText}>✕</Text>
              </Pressable>
            </View>
            <ScrollView contentContainerStyle={styles.sheetContent}>
              <TextInput
                style={styles.notesInput}
                multiline
                value={notesValue}
                onChangeText={setNotesValue}
                placeholder="Protocol notes…"
                placeholderTextColor={c.slate}
                textAlignVertical="top"
              />
              <Pressable style={styles.primaryButton} onPress={() => void saveNotes()}>
                <Text style={styles.primaryButtonText}>Save Notes</Text>
              </Pressable>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Log sheet (recent completions) */}
      <Modal visible={showLogSheet} transparent animationType="slide" onRequestClose={() => setShowLogSheet(false)}>
        <View style={styles.sheetBackdrop}>
          <View style={styles.sheet}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Protocol Log</Text>
              <Pressable onPress={() => setShowLogSheet(false)} style={styles.sheetClose}>
                <Text style={styles.sheetCloseText}>✕</Text>
              </Pressable>
            </View>
            {logsLoading ? (
              <ActivityIndicator color={t.goldBright} style={styles.logsLoading} />
            ) : logs.length === 0 ? (
              <Text style={styles.empty}>No log entries yet.</Text>
            ) : (
              <ScrollView contentContainerStyle={styles.sheetContent}>
                {logs.map((log) => (
                  <View key={log.id} style={styles.logRow}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
                    <View style={styles.logRowBody}>
                      <Text style={styles.logDate}>
                        {formatLogDate(log.started_at)} → {formatLogDate(log.completed_at)}
                      </Text>
                      <Text style={styles.logMeta}>
                        {log.steps_completed} steps · {formatDurationSeconds(log.total_duration_seconds)}
                        {log.pause_count ? ` · ${log.pause_count} pause${log.pause_count === 1 ? '' : 's'}` : ''}
                      </Text>
                    </View>
                    <Text
                      style={[
                        styles.logBadge,
                        log.status === 'success' ? styles.logBadgeSuccess : styles.logBadgeAbandoned,
                      ]}
                    >
                      {log.status}
                    </Text>
                  </View>
                ))}
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Schedule sheet: recurring day/time instances — mirrors web's
          src/app/protocols/[protocolId]/page.tsx Schedule Sheet (same
          protocol_schedules row shape: instances[] of { id, time, days }). */}
      <Modal
        visible={showScheduleSheet}
        transparent
        animationType="slide"
        onRequestClose={() => setShowScheduleSheet(false)}
      >
        <View style={styles.sheetBackdrop}>
          <View style={styles.sheet}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Schedule</Text>
              <Pressable onPress={() => setShowScheduleSheet(false)} style={styles.sheetClose}>
                <Text style={styles.sheetCloseText}>✕</Text>
              </Pressable>
            </View>
            {scheduleLoading ? (
              <ActivityIndicator color={t.goldBright} style={styles.logsLoading} />
            ) : (
              <ScrollView contentContainerStyle={styles.sheetContent}>
                {(schedule?.instances ?? []).length === 0 ? (
                  <Text style={styles.empty}>No scheduled instances yet.</Text>
                ) : null}
                {(schedule?.instances ?? []).map((instance) => (
                  <ScheduleInstanceRow
                    key={instance.id}
                    instance={instance}
                    onChangeTime={(time) => handleUpdateScheduleInstanceTime(instance.id, time)}
                    onToggleDay={(day) => handleToggleScheduleDay(instance.id, day)}
                    onDelete={() => confirmDeleteScheduleInstance(instance.id)}
                  />
                ))}
                <Pressable style={styles.outlineButton} onPress={handleAddScheduleInstance}>
                  <Text style={styles.outlineButtonText}>+ Add Schedule</Text>
                </Pressable>
              </ScrollView>
            )}
          </View>
        </View>
      </Modal>

      {/* Settings sheet: transition */}
      <Modal
        visible={showSettingsSheet}
        transparent
        animationType="slide"
        onRequestClose={() => setShowSettingsSheet(false)}
      >
        <View style={styles.sheetBackdrop}>
          <View style={styles.sheet}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Settings</Text>
              <Pressable onPress={() => setShowSettingsSheet(false)} style={styles.sheetClose}>
                <Text style={styles.sheetCloseText}>✕</Text>
              </Pressable>
            </View>
            <View style={styles.sheetContent}>
              <View style={styles.settingsRow}>
                <Switch value={transitionEnabled} onValueChange={(v) => void toggleTransition(v)} />
                <Text style={styles.settingsLabel}>Transition</Text>
                <TextInput
                  style={styles.settingsSecondsInput}
                  keyboardType="numeric"
                  value={transitionSeconds}
                  onChangeText={setTransitionSeconds}
                  onEndEditing={(e) => void commitTransitionSeconds(e.nativeEvent.text)}
                />
                <Text style={styles.settingsLabel}>sec</Text>
              </View>
              <Text style={styles.settingsHint}>
                When enabled, a countdown starts after each step&apos;s timer runs out. If you don&apos;t tap Next or
                Repeat before it ends, the step auto-completes and the next one begins.
              </Text>
            </View>
          </View>
        </View>
      </Modal>

      {/* Step notes sheet */}
      <Modal
        visible={!!stepNotesStepId}
        transparent
        animationType="slide"
        onRequestClose={() => setStepNotesStepId(null)}
      >
        <View style={styles.sheetBackdrop}>
          <View style={styles.sheet}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle} numberOfLines={1}>
                Step Notes — {sortedSteps.find((s) => s.id === stepNotesStepId)?.title ?? ''}
              </Text>
              <Pressable onPress={() => setStepNotesStepId(null)} style={styles.sheetClose}>
                <Text style={styles.sheetCloseText}>✕</Text>
              </Pressable>
            </View>
            <ScrollView contentContainerStyle={styles.sheetContent}>
              <TextInput
                style={styles.notesInput}
                multiline
                value={stepNotesValue}
                onChangeText={setStepNotesValue}
                placeholder="Notes for this step…"
                placeholderTextColor={c.slate}
                textAlignVertical="top"
              />
              <Pressable style={styles.primaryButton} onPress={() => void saveStepNotes()}>
                <Text style={styles.primaryButtonText}>Save Notes</Text>
              </Pressable>
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Timer expired dialog */}
      <Modal visible={showTimerDialog} transparent animationType="fade">
        <View style={styles.modalBackdrop}>
          <View style={styles.timerDialogCard}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
            <Text style={styles.timerDialogTitle}>Step Complete!</Text>
            <Text style={styles.timerDialogBody}>&quot;{activeStepTitle}&quot; time is up.</Text>
            {transitionEnabled && transitionCountdown !== null && transitionCountdown > 0 ? (
              <Text style={styles.timerDialogCountdown}>Auto-advancing in {formatTimer(transitionCountdown)}</Text>
            ) : null}
            <View style={styles.timerDialogActions}>
              <Pressable style={styles.outlineButton} onPress={handleRepeat}>
                <Text style={styles.outlineButtonText}>Repeat</Text>
              </Pressable>
              <Pressable style={styles.primaryButton} onPress={() => void handleNext()}>
                <Text style={styles.primaryButtonText}>Next</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

function StepRow({
  step,
  index,
  count,
  isActive,
  onToggleDone,
  onMoveUp,
  onMoveDown,
  onTitleChange,
  onDurationChange,
  onOpenNotes,
  onDelete,
}: {
  step: ProtocolItemRow;
  index: number;
  count: number;
  isActive: boolean;
  onToggleDone: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onTitleChange: (title: string) => void;
  onDurationChange: (text: string) => void;
  onOpenNotes: () => void;
  onDelete: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [titleDraft, setTitleDraft] = useState(step.title);
  const [durationDraft, setDurationDraft] = useState(step.duration_minutes != null ? String(step.duration_minutes) : '');

  useEffect(() => {
    setTitleDraft(step.title);
  }, [step.title]);
  useEffect(() => {
    setDurationDraft(step.duration_minutes != null ? String(step.duration_minutes) : '');
  }, [step.duration_minutes]);

  return (
    <View style={[styles.stepRow, isActive && styles.stepRowActive, step.done && styles.stepRowDone]}>
      <View style={styles.moveCol}>
        <Pressable disabled={index === 0} onPress={onMoveUp}>
          <Text style={[styles.moveArrow, index === 0 && styles.moveArrowDisabled]}>▲</Text>
        </Pressable>
        <Pressable disabled={index === count - 1} onPress={onMoveDown}>
          <Text style={[styles.moveArrow, index === count - 1 && styles.moveArrowDisabled]}>▼</Text>
        </Pressable>
      </View>
      <Pressable style={[styles.stepDot, step.done && styles.stepDotDone]} onPress={onToggleDone} />
      <TextInput
        style={[styles.stepTitleInput, step.done && styles.stepTitleDone]}
        value={titleDraft}
        onChangeText={setTitleDraft}
        onEndEditing={() => onTitleChange(titleDraft)}
        placeholder="Step name…"
        placeholderTextColor={c.slate}
      />
      <TextInput
        style={styles.stepDurationInput}
        value={durationDraft}
        onChangeText={setDurationDraft}
        onEndEditing={() => onDurationChange(durationDraft)}
        keyboardType="numeric"
        placeholder="min"
        placeholderTextColor={c.slate}
      />
      <Pressable style={styles.stepIconButton} onPress={onOpenNotes}>
        <Text style={styles.stepIconText}>Notes</Text>
      </Pressable>
      <Pressable style={styles.stepIconButton} onPress={onDelete}>
        <Text style={[styles.stepIconText, styles.stepIconDanger]}>Del</Text>
      </Pressable>
    </View>
  );
}

// One recurring day/time schedule instance — time input + day-of-week
// toggle chips + delete, matching web's Schedule Sheet row
// (src/app/protocols/[protocolId]/page.tsx: Input type="time" + DAYS_OF_WEEK
// Checkboxes + Trash2 delete button).
function ScheduleInstanceRow({
  instance,
  onChangeTime,
  onToggleDay,
  onDelete,
}: {
  instance: ProtocolScheduleInstance;
  onChangeTime: (time: string) => void;
  onToggleDay: (day: string) => void;
  onDelete: () => void;
}) {
  const { t, c } = useTheme();
  const styles = useStyles();
  const [timeDraft, setTimeDraft] = useState(instance.time);

  useEffect(() => {
    setTimeDraft(instance.time);
  }, [instance.time]);

  return (
    <View style={styles.scheduleCard}>
<LinearGradient colors={t.panelGradient} start={{ x: 0.2, y: 0 }} end={{ x: 0.8, y: 1 }} style={StyleSheet.absoluteFill} pointerEvents="none" />
      <View style={styles.scheduleCardHeader}>
        <TextInput
          style={styles.scheduleTimeInput}
          value={timeDraft}
          onChangeText={setTimeDraft}
          onEndEditing={() => onChangeTime(timeDraft)}
          placeholder="09:00"
          placeholderTextColor={c.slate}
          keyboardType="numbers-and-punctuation"
        />
        <Pressable style={styles.stepIconButton} onPress={onDelete}>
          <Text style={[styles.stepIconText, styles.stepIconDanger]}>Del</Text>
        </Pressable>
      </View>
      <View style={styles.chipRow}>
        {DAYS_OF_WEEK.map((day) => (
          <Chip key={day} text={day.slice(0, 3)} active={instance.days.includes(day)} onPress={() => onToggleDay(day)} />
        ))}
      </View>
    </View>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu, radii }) =>
  StyleSheet.create({
  root: { flex: 1, padding: 12 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24, gap: 8 },
  error: { color: c.danger, marginBottom: 8, fontSize: 13 },
  empty: { color: c.slate, fontStyle: 'italic', textAlign: 'center', marginTop: 24 },
  emptyTitle: { fontSize: 16, fontWeight: '800', color: c.text, textAlign: 'center', fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  emptyHint: { fontSize: 13, color: c.slate, textAlign: 'center', lineHeight: 18 },

  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 },
  headerTitle: { fontSize: 18, color: t.ivory, letterSpacing: 2.5, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  addButton: {
    backgroundColor: c.lime,
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 8,
  },
  addButtonText: { fontWeight: '900', color: t.goldBright },

  listScroll: { flex: 1 },
  listContent: { gap: 6, paddingBottom: 16 },

  protocolRow: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: t.glass,
    borderRadius: 14,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  protocolTouch: { flex: 1, flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },
  protocolTitle: { flex: 1, fontSize: 15, fontWeight: '700', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  chevronRight: { fontSize: 18, color: c.slate, marginLeft: 4 },

  moveCol: { gap: 2 },
  moveArrow: { fontSize: 12, color: t.goldBright, paddingHorizontal: 4, paddingVertical: 2 },
  moveArrowDisabled: { color: c.glassRim },

  rowMenuButton: { paddingHorizontal: 8, paddingVertical: 4 },
  rowMenuText: { fontSize: 18, color: c.slate, fontWeight: '900' },

  groupBlock: { marginTop: 10, gap: 6 },
  groupHeaderRow: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: t.glass,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  groupHeaderTouch: { flex: 1, flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 8 },
  groupChevron: { fontSize: 13, color: c.slate },
  groupName: { fontSize: 14, fontWeight: '800', color: c.text, flexShrink: 1 },
  groupCount: { fontSize: 12, color: c.slate },
  emptyGroupText: { color: c.slate, fontStyle: 'italic', fontSize: 13, paddingHorizontal: 8 },

  // Modal shared
  modalBackdrop: { flex: 1, backgroundColor: c.overlay, alignItems: 'center', justifyContent: 'center', padding: 24 },
  formCard: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.navyRaised,
    borderRadius: 22,
    padding: 18,
    width: '100%',
    maxWidth: 380,
    gap: 10,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  formTitle: { fontSize: 16, fontWeight: '900', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  formLabel: { fontSize: 12, fontWeight: '700', color: c.slate, marginTop: 4 },
  formInput: {
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 9,
    fontSize: 15,
    color: c.text,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  formActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  formCancel: { paddingHorizontal: 12, paddingVertical: 10 },
  formCancelText: { color: c.slate, fontWeight: '700' },
  formSubmit: { backgroundColor: c.lime, borderRadius: 14, paddingHorizontal: 16, paddingVertical: 10 },
  formSubmitDisabled: { opacity: 0.5 },
  formSubmitText: { color: t.goldBright, fontWeight: '900' },

  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  chip: {
    backgroundColor: t.glass,
    borderRadius: radii.pill,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  chipActive: { backgroundColor: t.gold, borderColor: t.gold, boxShadow: `0 4px 14px ${t.goldGlow}` },
  chipText: { fontSize: 12, fontWeight: '700', color: c.slate },
  chipTextActive: { color: t.onGold },

  menuCard: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.navyRaised,
    borderRadius: 22,
    paddingVertical: 6,
    width: '100%',
    maxWidth: 320,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  menuItem: { paddingHorizontal: 18, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: t.lineFaint },
  menuItemText: { fontSize: 15, fontWeight: '700', color: c.text },
  menuItemDanger: { color: c.danger },

  // Detail header + controls
  detailHeader: { flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 8 },
  backButton: { paddingVertical: 6, paddingRight: 8 },
  backText: { fontSize: 15, color: t.goldBright, fontWeight: '700' },
  detailTitle: { flex: 1, fontSize: 17, fontWeight: '900', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  menuButton: { paddingHorizontal: 8, paddingVertical: 6 },

  controlsRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' },
  timerBadge: {
    fontVariant: ['tabular-nums'],
    fontSize: 15,
    fontWeight: '900',
    color: c.text,
    backgroundColor: c.limeSoft,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 14,
  },
  primaryButton: { backgroundColor: c.lime, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 8, ...neu.raisedSm },
  primaryButtonText: { color: t.goldBright, fontWeight: '900', fontSize: 13 },
  secondaryButton: { backgroundColor: c.gearPeach },
  secondaryButtonText: { color: c.slate },
  outlineButton: {
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingVertical: 8,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  outlineButtonText: { color: t.goldBright, fontWeight: '800', fontSize: 13 },

  runnerControlsRow: { flexDirection: 'row', gap: 8, marginBottom: 6 },
  smallButton: {
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  smallButtonText: { color: t.goldBright, fontWeight: '800', fontSize: 12 },
  smallDangerButton: {
    borderRadius: 14,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  smallDangerButtonText: { color: c.danger, fontWeight: '800', fontSize: 12 },

  transitionHint: { fontSize: 12, color: c.slate, textAlign: 'center', marginBottom: 6, fontStyle: 'italic' },

  stepsScroll: { flex: 1 },
  stepsContent: { gap: 6, paddingBottom: 16 },

  stepRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'transparent',
    borderRadius: 14,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: t.lineFaint,
  },
  stepRowActive: { backgroundColor: c.lime, borderLeftWidth: 3, borderLeftColor: t.gold },
  stepRowDone: { opacity: 0.55 },
  stepDot: {
    width: 18,
    height: 18,
    borderRadius: 9,
    borderWidth: 2,
    borderColor: c.slate,
  },
  stepDotDone: { backgroundColor: t.gold, borderColor: t.gold, boxShadow: `0 0 8px ${t.goldGlow}` },
  stepTitleInput: {
    flex: 1,
    fontSize: 14,
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  stepTitleDone: { textDecorationLine: 'line-through', color: c.slate },
  stepDurationInput: {
    width: 52,
    fontSize: 13,
    color: c.text,
    textAlign: 'center',
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 6,
    paddingVertical: 7,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  stepIconButton: { paddingHorizontal: 6, paddingVertical: 4 },
  stepIconText: { fontSize: 11, color: t.goldBright, fontWeight: '700' },
  stepIconDanger: { color: c.danger },

  addStepRow: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 4,
    backgroundColor: t.glass,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  addStepTitleInput: {
    flex: 1,
    fontSize: 14,
    color: c.text,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 7,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  addStepDurationInput: {
    width: 52,
    fontSize: 13,
    color: c.text,
    textAlign: 'center',
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 6,
    paddingVertical: 7,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  addStepButton: { paddingHorizontal: 10, paddingVertical: 6 },
  addStepButtonText: { color: t.goldBright, fontWeight: '900', fontSize: 13 },

  // Bottom sheets
  sheetBackdrop: { flex: 1, backgroundColor: c.overlay, justifyContent: 'flex-end' },
  sheet: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.navyRaised,
    borderTopLeftRadius: radii.panel,
    borderTopRightRadius: radii.panel,
    maxHeight: '86%',
    minHeight: '40%',
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  sheetHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', paddingVertical: 16 },
  sheetTitle: { fontSize: 16, fontWeight: '900', color: c.text, paddingHorizontal: 40, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  sheetClose: { position: 'absolute', right: 16, top: 12, padding: 4 },
  sheetCloseText: { fontSize: 18, color: c.slate },
  sheetContent: { paddingHorizontal: 16, paddingBottom: 32, gap: 12 },

  notesInput: {
    minHeight: 220,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    padding: 12,
    fontSize: 14,
    color: c.text,
    borderWidth: 1,
    borderColor: c.inputRim,
  },

  logsLoading: { marginTop: 24 },
  logRow: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: 14,
    padding: 12,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  logRowBody: { flex: 1 },
  logDate: { fontSize: 13, color: c.text, fontWeight: '700' },
  logMeta: { fontSize: 12, color: c.slate, marginTop: 2 },
  logBadge: { fontFamily: DISPLAY_FONT,
    fontSize: 11,
    fontWeight: '900',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: radii.pill,
    overflow: 'hidden',
    textTransform: 'uppercase',
  },
  logBadgeSuccess: { backgroundColor: c.lime, color: t.goldBright },
  logBadgeAbandoned: { backgroundColor: c.gearPeach, color: c.slate },

  scheduleCard: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.glass,
    borderRadius: 14,
    padding: 12,
    gap: 10,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  scheduleCardHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  scheduleTimeInput: {
    backgroundColor: c.inputFill,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 6,
    fontSize: 14,
    color: c.text,
    width: 90,
    borderWidth: 1,
    borderColor: c.inputRim,
  },

  settingsRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  settingsLabel: { fontSize: 13, fontWeight: '700', color: c.text },
  settingsSecondsInput: {
    width: 56,
    borderRadius: 14,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 14,
    color: c.text,
    backgroundColor: c.inputFill,
    borderWidth: 1,
    borderColor: c.inputRim,
  },
  settingsHint: { fontSize: 12, color: c.slate, lineHeight: 17 },

  timerDialogCard: {
    overflow: 'hidden',
    boxShadow: `0 10px 30px ${t.glowSoft}`,
    backgroundColor: t.navyRaised,
    borderRadius: 22,
    padding: 20,
    width: '100%',
    maxWidth: 360,
    gap: 10,
    borderWidth: 1,
    borderColor: t.navyLine,
  },
  timerDialogTitle: { fontSize: 17, fontWeight: '900', color: c.text, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  timerDialogBody: { fontSize: 14, color: c.text, lineHeight: 20 },
  timerDialogCountdown: { fontSize: 15, fontWeight: '900', color: t.goldBright, fontVariant: ['tabular-nums'] },
  timerDialogActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 10, marginTop: 8 },
  }),
);
